(() => {
  'use strict';

  const instances = new WeakMap();

  function findPrevNext(root) {
    let prev = root.querySelector('[data-carousel-prev]');
    let next = root.querySelector('[data-carousel-next]');
    if (!prev || !next) {
      // Fallback: find by arrow icons inside buttons
      root.querySelectorAll('button').forEach(btn => {
        if (!prev && btn.querySelector('.lucide-arrow-left')) prev = btn;
        if (!next && btn.querySelector('.lucide-arrow-right')) next = btn;
      });
    }
    return { prev, next };
  }

  function setupScrollMode(root, viewport, prev, next) {
    if (!viewport.hasAttribute('tabindex')) viewport.setAttribute('tabindex', '0');

    function ratio() {
      const r = parseFloat(viewport.getAttribute('data-scroll-ratio') || '0.9');
      return isNaN(r) ? 0.9 : r;
    }
    function step() { return viewport.clientWidth * ratio(); }
    function atStart() { return viewport.scrollLeft <= 1; }
    function atEnd() { return (viewport.scrollLeft + viewport.clientWidth) >= (viewport.scrollWidth - 1); }

    function update() {
      if (prev) prev.disabled = atStart();
      if (next) next.disabled = atEnd();
    }

    function onPrev() { viewport.scrollBy({ left: -step(), behavior: 'smooth' }); }
    function onNext() { viewport.scrollBy({ left:  step(), behavior: 'smooth' }); }

    function onKey(e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); onPrev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); onNext(); }
    }

    function onWheel(e) {
      // Shift+wheel to scroll horizontally if needed
      if (e.shiftKey) {
        viewport.scrollBy({ left: e.deltaY });
        e.preventDefault();
      }
    }

    if (prev) prev.addEventListener('click', onPrev);
    if (next) next.addEventListener('click', onNext);
    viewport.addEventListener('scroll', update, { passive: true });
    viewport.addEventListener('keydown', onKey);
    viewport.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('resize', update);

    // Update when images load (widths can change)
    viewport.querySelectorAll('img').forEach(img => img.addEventListener('load', update, { once: true }));

    update();

    return () => {
      if (prev) prev.removeEventListener('click', onPrev);
      if (next) next.removeEventListener('click', onNext);
      viewport.removeEventListener('scroll', update);
      viewport.removeEventListener('keydown', onKey);
      viewport.removeEventListener('wheel', onWheel);
      window.removeEventListener('resize', update);
    };
  }

  function setupTransformMode(root, viewport, track, slides, prev, next) {
    if (!viewport.hasAttribute('tabindex')) viewport.setAttribute('tabindex', '0');

    let index = 0;
    let slideStep = 1;
    let maxIndex = 0;
    let translate = 0;
    let dragging = false;
    let startX = 0;
    let startTranslate = 0;

    function measure() {
      if (!slides.length) return;

      // Base step as first slide width
      let w = slides[0].getBoundingClientRect().width;

      // If second slide exists, use delta between slide lefts (more accurate with gutters)
      if (slides.length > 1) {
        const r0 = slides[0].getBoundingClientRect();
        const r1 = slides[1].getBoundingClientRect();
        const delta = Math.round(r1.left - r0.left);
        if (delta > 0) w = delta;
      }

      slideStep = Math.max(1, w);
      const vpW = viewport.getBoundingClientRect().width;
      const perView = Math.max(1, Math.round(vpW / slideStep));
      maxIndex = Math.max(0, slides.length - perView);

      index = Math.min(index, maxIndex);
      translate = -index * slideStep;
      setTransform(true);
      updateButtons();
    }

    function setTransform(noTransition) {
      track.style.transition = noTransition ? 'none' : 'transform 300ms ease';
      track.style.transform = `translate3d(${translate}px,0,0)`;
    }

    function snapTo(i) {
      index = Math.max(0, Math.min(i, maxIndex));
      translate = -index * slideStep;
      setTransform(false);
      updateButtons();
    }

    function nextClick() { snapTo(index + 1); }
    function prevClick() { snapTo(index - 1); }

    function updateButtons() {
      if (prev) prev.disabled = index <= 0;
      if (next) next.disabled = index >= maxIndex;
    }

    function onPointerDown(e) {
      dragging = true;
      try { viewport.setPointerCapture(e.pointerId); } catch (_) {}
      track.style.transition = 'none';
      startX = e.clientX;
      startTranslate = translate;
      viewport.classList.add('is-dragging');
    }

    function onPointerMove(e) {
      if (!dragging) return;
      const dx = e.clientX - startX;
      translate = startTranslate + dx;

      const minT = -maxIndex * slideStep;
      const maxT = 0;
      if (translate < minT) translate = minT;
      if (translate > maxT) translate = maxT;

      setTransform(true);
    }

    function onPointerUp(e) {
      if (!dragging) return;
      dragging = false;
      try { viewport.releasePointerCapture(e.pointerId); } catch (_) {}
      viewport.classList.remove('is-dragging');
      const newIndex = Math.round(-translate / slideStep);
      snapTo(newIndex);
    }

    function onKey(e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); prevClick(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); nextClick(); }
    }

    if (prev) prev.addEventListener('click', prevClick);
    if (next) next.addEventListener('click', nextClick);
    viewport.addEventListener('pointerdown', onPointerDown, { passive: true });
    viewport.addEventListener('pointermove', onPointerMove);
    viewport.addEventListener('pointerup', onPointerUp);
    viewport.addEventListener('pointercancel', onPointerUp);
    viewport.addEventListener('keydown', onKey);
    window.addEventListener('resize', measure);

    slides.forEach(slide => {
      slide.querySelectorAll('img').forEach(img => img.addEventListener('load', measure));
    });

    measure();

    return () => {
      if (prev) prev.removeEventListener('click', prevClick);
      if (next) next.removeEventListener('click', nextClick);
      viewport.removeEventListener('pointerdown', onPointerDown);
      viewport.removeEventListener('pointermove', onPointerMove);
      viewport.removeEventListener('pointerup', onPointerUp);
      viewport.removeEventListener('pointercancel', onPointerUp);
      viewport.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', measure);
    };
  }

  function initOne(root) {
    if (instances.has(root)) return;

    // Find slides by ARIA (reliable in your markup)
    const slides = root.querySelectorAll('[aria-roledescription="slide"]');
    if (!slides.length) return;

    // Viewport: either custom class or parent of slides’ container
    let viewport = root.querySelector('.carousel-viewport');
    let track = root.querySelector('.carousel-track');

    if (!track) track = slides[0].parentElement;
    if (!viewport) viewport = track ? track.parentElement : null;
    if (!viewport || !track) return;

    const { prev, next } = findPrevNext(root);

    // Decide mode: scroll or transform (based on overflowX)
    const overflowX = getComputedStyle(viewport).overflowX;
    const canScroll = (overflowX === 'auto' || overflowX === 'scroll') && (viewport.scrollWidth - viewport.clientWidth > 2);

    // If developer forgot overflow-x, we still force working by transform mode
    let destroy;
    if (canScroll) {
      destroy = setupScrollMode(root, viewport, prev, next);
    } else {
      destroy = setupTransformMode(root, viewport, track, slides, prev, next);
    }

    instances.set(root, { destroy });
  }

  function initAll(scope) {
    const roots = (scope || document).querySelectorAll('[aria-roledescription="carousel"]');
    roots.forEach(initOne);
  }

  function unload(scope) {
    const roots = (scope || document).querySelectorAll('[aria-roledescription="carousel"]');
    roots.forEach(root => {
      const inst = instances.get(root);
      if (inst && inst.destroy) inst.destroy();
      instances.delete(root);
    });
  }

  // Auto init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Shopify editor events
  document.addEventListener('shopify:section:load', e => initAll(e.target));
  document.addEventListener('shopify:section:unload', e => unload(e.target));

  // Expose for manual re-init if needed
  window.PastWinnersCarousel = { initAll, unload };
})();